import React, {useEffect, useState} from "react";
import {Form, Spinner, Table} from "react-bootstrap";
import {EnfluxMLModel, useEnfluxModelsBySchool} from "@/hooks/categorizer";
import {ColumnDef, flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import {EAIClient} from "Services/EAIClient";
import {useFeatureGating} from "@/hooks/feature-gating";
import {useAppContext} from "@/contexts/app";

export default function CategorizerModel({schoolId}: { schoolId: string }) {
    const {data: mlModelsData, mutate} = useEnfluxModelsBySchool(schoolId);
    const {data: featuresData, mutate: mutateFeatures} = useFeatureGating(schoolId);
    const [aiModelEnabled, setAiModelEnabled] = useState(false);
    const [uiModelEnabled, setUiModelEnabled] = useState(false);
    const {dispatchError} = useAppContext();
    const client = new EAIClient();

    const handleEnableFeature = async (component: string, name: string, enabled: boolean) => {
        try {
            await client.enableSchoolFeature(schoolId, component, name, enabled);
            await mutateFeatures();
        } catch (e) {
            dispatchError(e);
        }
    };

    const handleAiModelEnable = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        await handleEnableFeature("ai", "categorization", ev.target.checked);
    };

    const handleUiModelEnable = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        await handleEnableFeature("ui", "categorizer", ev.target.checked);
    };

    useEffect(() => {
        if (featuresData) {
            featuresData.data.forEach(feature => {
                if (feature.component === "ai" && feature.name === "categorization") {
                    setAiModelEnabled(feature.enabled);
                } else if (feature.component === "ui" && feature.name === "categorizer") {
                    setUiModelEnabled(feature.enabled);
                }
            });
        }
    }, [featuresData]);

    return (
        <div className="list list-container">
            <div className="pt-3 pb-3">
                <h5>Features</h5>
                <Form.Check
                    checked={aiModelEnabled}
                    onChange={handleAiModelEnable}
                    className="mb-2"
                    label="AI Model Categorizer"/>
                <Form.Check
                    checked={uiModelEnabled}
                    onChange={handleUiModelEnable}
                    label="User Interface Categorizer"/>
            </div>
            <h5>Models</h5>
            {mlModelsData
                ? <EnfluxMLModelReactTable data={mlModelsData.data} onSubmit={() => mutate()} schoolId={schoolId}/>
                : <Spinner animation="border" variant="success"/>}
        </div>
    );
}

function EnfluxMLModelReactTable({data: defaultData, onSubmit, schoolId}: {
    data: EnfluxMLModel[],
    schoolId: string,
    onSubmit: () => void
}) {
    const [data, setData] = useState<EnfluxMLModel[]>(() => [...defaultData]);
    const [loading, setLoading] = useState<string[]>([]);
    const client = new EAIClient();

    const columns: ColumnDef<EnfluxMLModel>[] = [
        {
            header: "Enabled",
            accessorKey: "enabled",
            cell: context => {
                const handleOnChange = async (enabled: boolean) => {
                    await handleEnableModel(context.row.original, enabled);
                };
                if (loading.includes(context.row.original.ruuid)) {
                    return <Spinner size="sm" variant="success" />;
                }
                return <SimpleCheckbox
                    isChecked={context.row.original.enabled || false}
                    id={"btn-check-" + context.row.original.ruuid}
                    onChange={handleOnChange}/>;
            }
        },
        {
            header: "Name",
            accessorKey: "name"
        },
        {
            header: "Description",
            accessorKey: "description"
        },
        {
            header: "Groups",
            cell: context => {
                return context.row.original.groups ? context.row.original.groups.join(", ") : "";
            }
        }
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        manualFiltering: true,
    });

    useEffect(() => {
        setData([...defaultData]);
    }, [defaultData]);

    const handleEnableModel = async (model: EnfluxMLModel, enabled: boolean) => {
        setLoading([...loading, model.ruuid]);
        await Promise.all([
            client.enableEnfluxMLModel(schoolId, model.ruuid, enabled),
            new Promise(resolve => setTimeout(resolve, 320)),
        ]);
        setData(data.map(m => m.ruuid === model.ruuid ? {...m, enabled} : m));
        onSubmit();
        setLoading(loading.filter(ruuid => ruuid !== model.ruuid));
    };

    return (
        <>
            <Table responsive>
                <thead>
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                            <th key={header.id}>
                                {header.isPlaceholder
                                    ? null
                                    : flexRender(
                                        header.column.columnDef.header,
                                        header.getContext()
                                    )}
                            </th>
                        ))}
                    </tr>
                ))}
                </thead>
                <tbody>
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id}>
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </Table>
        </>
    );
}
